import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { saveAs } from 'file-saver';
import ExcelJS from 'exceljs';
import { FilterIcon } from 'lucide-react';
import { MdOutlineDownload } from 'react-icons/md';
import i18n from 'src/i18n';
import { getSurveyQuestionByQuarterByPage } from 'src/services/question';
import { DataMgmtCatalogPagination } from 'src/components/configuration/dataMgmtCatalogUi';

import '../controls/style.css';
import '../business-model-inventory/style.css';
import '../DocumentManagement/DocumentManagement.css';
import './ExportSurvey.css';

const EXPORT_POPUP_MAX_W = 400;
const EXPORT_POPUP_GAP = 4;
const EXPORT_POPUP_PAD = 8;

function getExportSurveyPopupStyle(
  anchor: { top: number; left: number; right: number },
  rtl: boolean,
): CSSProperties {
  if (typeof window === 'undefined') {
    return { position: 'fixed', top: anchor.top + EXPORT_POPUP_GAP };
  }
  const vw = window.innerWidth;
  const y = anchor.top + EXPORT_POPUP_GAP;

  if (rtl) {
    const effectiveLeft = Math.max(anchor.left, EXPORT_POPUP_PAD);
    const maxW = Math.min(
      EXPORT_POPUP_MAX_W,
      Math.max(220, vw - effectiveLeft - EXPORT_POPUP_PAD),
    );
    return {
      position: 'fixed',
      top: y,
      left: effectiveLeft,
      right: 'auto',
      maxWidth: maxW,
    };
  }

  const effectiveRight = Math.min(anchor.right, vw - EXPORT_POPUP_PAD);
  const rightFromViewport = vw - effectiveRight;
  const maxW = Math.min(
    EXPORT_POPUP_MAX_W,
    Math.max(220, effectiveRight - EXPORT_POPUP_PAD),
  );
  return {
    position: 'fixed',
    top: y,
    left: 'auto',
    right: rightFromViewport,
    maxWidth: maxW,
  };
}

const riskLevelColors: Record<string, string> = {
  Most: 'C00000',
  Significant: 'FF0000',
  Moderate: 'FFC000',
  Minimal: '00B050',
  Least: '92D050',
  'N/A': 'FFFFFF',
};

const riskLevelColorsForTable: Record<string, string> = {
  Most: '#C00000',
  Significant: '#FF0000',
  Moderate: '#FFC000',
  Minimal: '#00B050',
  Least: '#92D050',
  'N/A': '#FFFFFF',
};

const columnColors = {
  QID: '#ADD8E6',
  Category: '#fff',
  Question: '#fff',
  Owner: '#fff',
};

const ExportSurvey = () => {
  const { t } = useTranslation();
  const { id } = useParams();

  const [notAllowed, setNotAllowed] = useState(false);
  const [questions, setQuestions] = useState<any[]>([]);
  /** 0-based page index for the API */
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const currentYear = new Date().getFullYear();
  const [selectedQuarter, setSelectedQuarter] = useState('Q1');
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const filterBtnRef = useRef<HTMLButtonElement>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterAnchor, setFilterAnchor] = useState<{ top: number; left: number; right: number } | null>(
    null,
  );
  const [draftQuarter, setDraftQuarter] = useState('Q1');
  const [draftYear, setDraftYear] = useState(currentYear);

  const quarterOptions = useMemo(
    () => [
      { value: 'Q1', label: 'Q1' },
      { value: 'Q2', label: 'Q2' },
      { value: 'Q3', label: 'Q3' },
      { value: 'Q4', label: 'Q4' },
    ],
    [],
  );

  const yearOptions = useMemo(
    () =>
      Array.from({ length: 10 }, (_, i) => {
        const year = currentYear - i;
        return { value: year, label: String(year) };
      }),
    [currentYear],
  );

  const isRTL = i18n.language === 'ar';

  const updateFilterAnchor = useCallback(() => {
    if (filterBtnRef.current) {
      const r = filterBtnRef.current.getBoundingClientRect();
      setFilterAnchor({ top: r.bottom, left: r.left, right: r.right });
    }
  }, []);

  useLayoutEffect(() => {
    if (filterOpen) updateFilterAnchor();
    else setFilterAnchor(null);
  }, [filterOpen, updateFilterAnchor]);

  useEffect(() => {
    if (!filterOpen) return;
    const fn = () => updateFilterAnchor();
    window.addEventListener('scroll', fn, true);
    window.addEventListener('resize', fn);
    return () => {
      window.removeEventListener('scroll', fn, true);
      window.removeEventListener('resize', fn);
    };
  }, [filterOpen, updateFilterAnchor]);

  const filterPopupStyle = useMemo(
    () => (filterAnchor ? getExportSurveyPopupStyle(filterAnchor, isRTL) : undefined),
    [filterAnchor, isRTL],
  );

  const openFilterPanel = () => {
    setDraftQuarter(selectedQuarter);
    setDraftYear(selectedYear);
    setFilterOpen(true);
  };

  const applyFilterPanel = () => {
    setSelectedQuarter(draftQuarter);
    setSelectedYear(draftYear);
    setPage(0);
    setFilterOpen(false);
  };

  const resetFilterPanel = () => {
    setDraftQuarter('Q1');
    setDraftYear(currentYear);
    setSelectedQuarter('Q1');
    setSelectedYear(currentYear);
    setPage(0);
    setFilterOpen(false);
  };

  const filterData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await getSurveyQuestionByQuarterByPage(
        id,
        selectedQuarter,
        selectedYear,
        page,
        pageSize,
      );
      if (response?.success) {
        const d = response?.data ?? {};
        const result = Array.isArray(d.result) ? d.result : [];
        setQuestions(result);
        const tp = Number(d.totalPages);
        setTotalPages(Number.isFinite(tp) && tp > 0 ? tp : 1);
        const tcRaw = d.totalCount ?? d.totalRecords ?? d.totalElements ?? d.total;
        setTotalCount(typeof tcRaw === 'number' && !Number.isNaN(tcRaw) ? tcRaw : 0);
      } else {
        toast.error(String(response?.message ?? t('error.generic', 'Request failed')));
      }
    } catch (e: any) {
      const msg = e?.message ?? '';
      if (msg.includes('Forbidden')) {
        setNotAllowed(true);
      } else {
        toast.error(msg || String(t('error.generic', 'Request failed')));
      }
    } finally {
      setLoading(false);
    }
  }, [id, selectedQuarter, selectedYear, page, pageSize, t]);

  useEffect(() => {
    filterData();
  }, [filterData]);

  const exportToExcel = async () => {
    if (!id) return;
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Inherent Risk Profile');

      const response = await getSurveyQuestionByQuarterByPage(
        id,
        selectedQuarter,
        selectedYear,
        0,
        500,
      );

      if (!response?.success) {
        toast.error(String(response?.message ?? t('error.generic', 'Request failed')));
        return;
      }

      const data: any[] = Array.isArray(response?.data?.result) ? response.data.result : [];
      const data2 = data.map((row: any, index: number) => ({
        qid: index + 1,
        category: row?.question?.category?.name,
        question: row?.question?.question_en,
        owner: row?.user?.name,
        riskLevel: row?.answer,
      }));

      worksheet.columns = [
        {
          header: 'QID',
          key: 'qid',
          width: 10,
          style: {
            alignment: { vertical: 'middle', horizontal: 'center', wrapText: true },
            fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFADD8E6' } },
            border: {
              top: { style: 'thin' },
              left: { style: 'thin' },
              bottom: { style: 'thin' },
              right: { style: 'thin' },
            },
          },
        },
        {
          header: 'Category',
          key: 'category',
          width: 30,
          style: {
            alignment: { vertical: 'middle', horizontal: 'center', wrapText: true },
            fill: { type: 'pattern', pattern: 'solid' },
            border: {
              top: { style: 'thin' },
              left: { style: 'thin' },
              bottom: { style: 'thin' },
              right: { style: 'thin' },
            },
          },
        },
        {
          header: 'Question',
          key: 'question',
          width: 80,
          style: {
            alignment: { vertical: 'middle', horizontal: 'left', wrapText: true },
            fill: { type: 'pattern', pattern: 'solid' },
            border: {
              top: { style: 'thin' },
              left: { style: 'thin' },
              bottom: { style: 'thin' },
              right: { style: 'thin' },
            },
          },
        },
        {
          header: 'Owner',
          key: 'owner',
          width: 20,
          style: {
            alignment: { vertical: 'middle', horizontal: 'center', wrapText: true },
            fill: { type: 'pattern', pattern: 'solid' },
            border: {
              top: { style: 'thin' },
              left: { style: 'thin' },
              bottom: { style: 'thin' },
              right: { style: 'thin' },
            },
          },
        },
        {
          header: 'Risk Levels (Bank Response)',
          key: 'riskLevel',
          width: 25,
          style: {
            alignment: { vertical: 'middle', horizontal: 'center', wrapText: true },
            fill: { type: 'pattern', pattern: 'solid' },
            border: {
              top: { style: 'thin' },
              left: { style: 'thin' },
              bottom: { style: 'thin' },
              right: { style: 'thin' },
            },
          },
        },
      ];

      worksheet.getRow(1).height = 30;

      const headerRow = worksheet.getRow(1);
      headerRow.eachCell((cell: ExcelJS.Cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: '000000' },
        };
        cell.font = {
          color: { argb: 'FFFFFF' },
          bold: true,
        };
      });

      data2.forEach((row) => {
        const newRow = worksheet.addRow(row);
        const riskLevelCell = newRow.getCell('riskLevel');
        riskLevelCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: riskLevelColors[row.riskLevel] || 'FFFFFF' },
        };
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/octet-stream' });
      saveAs(blob, 'InherentRiskProfile.xlsx');
    } catch (e: any) {
      toast.error(e?.message || String(t('error.generic', 'Request failed')));
    }
  };

  const rangeStart = questions.length === 0 ? 0 : page * pageSize + 1;
  const rangeEnd = questions.length === 0 ? 0 : page * pageSize + questions.length;
  const displayTotalForMeta = totalCount > 0 ? totalCount : rangeEnd;
  const catalogTotalPages = Math.max(1, totalPages || 1);
  const totalFiltered = totalCount > 0 ? totalCount : rangeEnd;
  const pageOneBased = page + 1;

  const hasQuarterYearFilters = useMemo(
    () => selectedQuarter !== 'Q1' || selectedYear !== currentYear,
    [selectedQuarter, selectedYear, currentYear],
  );

  return (
    <div
      className={`document-management-container 
        document-management-page-wrap services-catalog-page  export-survey-page ${
        isRTL ? 'rtl' : ''
      }`}
    >
      <div
        className="container-fluid p-3 controls-catalog-main services-catalog-page-inner"
        style={{  zoom: 0.9 }}
      >
        {loading && (
          <div className="loading-overlay">
            <div className="spinner" />
            <span>{String(t('common.loading', { defaultValue: 'Loading...' }))}</span>
          </div>
        )}

        {!notAllowed && (
          <>
            <div className="services-catalog-page-header card border-0 shadow-sm mb-2">
              <div className="card-body py-2 px-3 px-lg-4">
                <div className="d-flex flex-column flex-xl-row align-items-stretch align-items-xl-center justify-content-between gap-3 w-100 services-catalog-header-main">
                  <div className="flex-grow-1 min-w-0" style={{ minWidth: 0 }}>
                    <h5 className="mb-1 fw-semibold text-dark">
                      {t('surveys.Export Survey', { defaultValue: 'Export survey' })}
                    </h5>
                    <p className="small text-muted mb-0">
                      {t('surveys.exportSurveySubtitle', {
                        defaultValue:
                          'Review assessment rows by quarter and year, then export the inherent risk profile to Excel.',
                      })}
                    </p>
                  </div>
                  <div
                    className={`d-flex flex-wrap gap-2 align-items-center flex-shrink-0 services-catalog-primary-actions services-catalog-header-actions-gap ${
                      isRTL ? 'flex-row-reverse' : ''
                    }`}
                  >
                    <button
                      type="button"
                      className="btn btn-create-service btn-sm d-inline-flex align-items-center justify-content-center gap-1"
                      onClick={exportToExcel}
                    >
                      <MdOutlineDownload size={16} aria-hidden />
                      {t('surveys.Export to Excel', { defaultValue: 'Export to Excel' })}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="services-catalog-table-toolbar controls-catalog-tools-card card border-0 shadow-sm mb-2">
              <div className="card-body py-2 px-3 px-lg-4">
                <div className="d-flex flex-column flex-md-row flex-wrap align-items-stretch align-items-md-center justify-content-between gap-3 services-catalog-toolbar-row">
                  <div className="d-flex flex-wrap align-items-center gap-3 gap-md-4">
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <span
                        className="small text-muted fw-semibold mb-0 text-uppercase"
                        style={{ fontSize: '0.7rem', letterSpacing: '0.04em' }}
                      >
                        {t('assets.totalRecords', { defaultValue: 'Total records' })}
                      </span>
                      <span className="fw-semibold text-dark services-catalog-toolbar-total-inline">
                        {displayTotalForMeta}
                      </span>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <label
                        className="small text-muted fw-semibold mb-0 text-uppercase"
                        style={{ fontSize: '0.7rem', letterSpacing: '0.04em' }}
                        htmlFor="export-survey-page-size"
                      >
                        {t('assets.pageSize', { defaultValue: 'Rows per page' })}
                      </label>
                      <select
                        id="export-survey-page-size"
                        className="form-select form-select-sm services-catalog-toolbar-select"
                        style={{ width: 'auto', minWidth: '4.5rem', maxWidth: '6rem' }}
                        value={pageSize}
                        onChange={(e) => {
                          setPageSize(Number(e.target.value));
                          setPage(0);
                        }}
                        dir={isRTL ? 'rtl' : 'ltr'}
                        aria-label={String(t('document_categories.rowsPerPage', 'Rows per page'))}
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={30}>30</option>
                      </select>
                    </div>
                    <span className="text-muted small">
                      {t('document_categories.showingRange', {
                        start: rangeStart,
                        end: rangeEnd,
                        total: displayTotalForMeta,
                        defaultValue: `Showing ${rangeStart}–${rangeEnd} of ${displayTotalForMeta}`,
                      })}
                    </span>
                  </div>
                  <div
                    className={`d-flex align-items-center justify-content-md-end gap-2 ms-md-auto ${
                      isRTL ? 'flex-row-reverse' : ''
                    }`}
                  >
                    <div
                      className={`controls-catalog-tab-pair ${filterOpen ? 'controls-catalog-tab-pair--open' : ''} ${
                        hasQuarterYearFilters ? 'controls-catalog-tab-pair--has-filters' : ''
                      }`}
                      role="group"
                      aria-label={String(t('common.filters', { defaultValue: 'Filters' }))}
                    >
                      <span className="controls-catalog-tab-pair__first">
                        {String(t('controls.catalogViewTab', { defaultValue: 'List' }))}
                      </span>
                      <button
                        ref={filterBtnRef}
                        type="button"
                        className="controls-catalog-tab-pair__filter"
                        onClick={() => (filterOpen ? setFilterOpen(false) : openFilterPanel())}
                        aria-label={String(t('controls.filter', { defaultValue: 'Filter' }))}
                        aria-expanded={filterOpen}
                        aria-pressed={filterOpen}
                      >
                        <FilterIcon size={18} aria-hidden />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="services-catalog-table-card card border-0 shadow-sm mb-2">
              <div className="table-responsive export-survey-table-wrap">
                <table className="table services-catalog-table mb-0 export-survey-table">
                  <thead>
                    <tr>
                      <th style={{ width: '4rem' }}>QID</th>
                      <th style={{ width: '14%' }}>{t('question.Category', { defaultValue: 'Category' })}</th>
                      <th>{t('question.Question', { defaultValue: 'Question' })}</th>
                      <th style={{ width: '12%' }}>{t('question.Owner', { defaultValue: 'Owner' })}</th>
                      <th style={{ width: '16%' }}>
                        {t('surveys.riskLevelsBankResponse', {
                          defaultValue: 'Risk levels (bank response)',
                        })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {questions.map((row: any, index: number) => (
                      <tr key={`${page}-${index}`}>
                        <td
                          className="text-center"
                          style={{ backgroundColor: columnColors.QID }}
                        >
                          {page * pageSize + index + 1}
                        </td>
                        <td className="text-center" style={{ backgroundColor: columnColors.Category }}>
                          {row?.question?.category?.name ?? '—'}
                        </td>
                        <td style={{ backgroundColor: columnColors.Question }}>
                          {row?.question?.question_en ?? '—'}
                        </td>
                        <td className="text-center" style={{ backgroundColor: columnColors.Owner }}>
                          {row?.user?.name ?? '—'}
                        </td>
                        <td
                          className="text-center export-survey-risk-cell"
                          style={{
                            backgroundColor:
                              riskLevelColorsForTable[row?.answer as keyof typeof riskLevelColorsForTable] ??
                              '#fff',
                          }}
                        >
                          {row?.answer ?? '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {totalFiltered > 0 && (
              <div className="mt-3">
                <DataMgmtCatalogPagination
                  page={pageOneBased}
                  totalPages={catalogTotalPages}
                  totalFiltered={totalFiltered}
                  busy={loading}
                  onPageChange={(p) => setPage(Math.max(0, p - 1))}
                />
              </div>
            )}
          </>
        )}

        {notAllowed && (
          <div className="container_info">
            <div className="d-flex justify-content-between my-2 text-center alert alert-danger">
              <h5 style={{ width: '100%' }} className="text-center">
                {t('permissions.not_allowed')}
              </h5>
            </div>
          </div>
        )}

        {typeof document !== 'undefined' &&
          createPortal(
            <>
              {filterOpen && filterAnchor && filterPopupStyle && (
                <div
                  className={`controls-catalog-popup-panel question-catalog-popup-layer ${
                    isRTL ? 'controls-catalog-popup-panel-rtl' : ''
                  }`}
                  style={{ ...filterPopupStyle, zIndex: 1060 }}
                >
                  <div className="controls-catalog-popup-header">
                    <span className="controls-catalog-popup-title">
                      {t('controls.filter', { defaultValue: 'Filter' })}
                    </span>
                    <button
                      type="button"
                      className="controls-catalog-popup-close"
                      onClick={() => setFilterOpen(false)}
                      aria-label={String(t('common.cancel', { defaultValue: 'Cancel' }))}
                    >
                      &times;
                    </button>
                  </div>
                  <div className={`controls-catalog-popup-body ${isRTL ? 'text-end' : 'text-start'}`}>
                    <div className="mb-3">
                      <label className="form-label" htmlFor="export-filter-quarter">
                        {t('surveys.quarter', { defaultValue: 'Quarter' })}
                      </label>
                      <select
                        id="export-filter-quarter"
                        className="form-select form-select-sm"
                        value={draftQuarter}
                        onChange={(e) => setDraftQuarter(e.target.value)}
                      >
                        {quarterOptions.map((q) => (
                          <option key={q.value} value={q.value}>
                            {q.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="mb-3">
                      <label className="form-label" htmlFor="export-filter-year">
                        {t('surveys.year', { defaultValue: 'Year' })}
                      </label>
                      <select
                        id="export-filter-year"
                        className="form-select form-select-sm"
                        value={draftYear}
                        onChange={(e) => setDraftYear(Number(e.target.value))}
                      >
                        {yearOptions.map((y) => (
                          <option key={y.value} value={y.value}>
                            {y.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div
                      className={`controls-catalog-popup-footer d-flex flex-wrap gap-2 justify-content-end ${
                        isRTL ? 'flex-row-reverse' : ''
                      }`}
                    >
                      <button type="button" className="btn btn-secondary btn-sm" onClick={resetFilterPanel}>
                        {t('controls.reset', { defaultValue: 'Reset' })}
                      </button>
                      <button type="button" className="btn btn-primary btn-sm" onClick={applyFilterPanel}>
                        {t('common.apply', { defaultValue: 'Apply' })}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>,
            document.body,
          )}
      </div>
    </div>
  );
};

export default ExportSurvey;
